/**
 * AnalyticsKpiStrip — headline KPI row built on the shared `StatsCards`.
 *
 * Pulls one figure from each enabled section. The underlying queries are shared
 * (deduped by React Query) with the detailed section cards below, so this strip
 * adds no extra network calls.
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import {Boxes, MapPinned, Store, Users} from 'lucide-react';
import StatsCards, {
  type StatCard
} from '../../../../Presentation/components/common/StatsCards';
import {
  useInventoryProductsAnalytics,
  useStoresUsersAnalytics,
  useVisitsAnalytics
} from '../hooks/useHomeAnalytics';

interface AnalyticsKpiStripProps {
  inventoryProductsEnabled: boolean;
  visitsEnabled: boolean;
  storesUsersEnabled: boolean;
}

/** Narrow the KPI card count to the grid widths `StatsCards` accepts (no cast). */
const columnsForCount = (count: number): 1 | 2 | 3 | 4 => {
  if (count >= 4) {
    return 4;
  }
  if (count === 3) {
    return 3;
  }
  if (count === 2) {
    return 2;
  }
  return 1;
};

const AnalyticsKpiStrip: React.FC<AnalyticsKpiStripProps> = ({
  inventoryProductsEnabled,
  visitsEnabled,
  storesUsersEnabled
}) => {
  const {t} = useTranslation();
  const inventoryProducts = useInventoryProductsAnalytics(
    inventoryProductsEnabled
  );
  const visits = useVisitsAnalytics(visitsEnabled);
  const storesUsers = useStoresUsersAnalytics(storesUsersEnabled);

  const cards: StatCard[] = [];

  if (storesUsersEnabled) {
    cards.push({
      title: t('analytics.kpi.total_stores'),
      value: storesUsers.data?.totalStores ?? 0,
      icon: <Store className="w-6 h-6 text-primary" />,
      subtitle: t('analytics.kpi.active_count', {
        count: storesUsers.data?.activeStores ?? 0
      })
    });
    cards.push({
      title: t('analytics.kpi.total_users'),
      value: storesUsers.data?.totalUsers ?? 0,
      icon: <Users className="w-6 h-6 text-success" />,
      bgColor: 'bg-success/10',
      textColor: 'text-success',
      subtitle: t('analytics.kpi.market_developers_count', {
        count: storesUsers.data?.marketDevelopers ?? 0
      })
    });
  }

  if (inventoryProductsEnabled) {
    cards.push({
      title: t('analytics.kpi.total_products'),
      value: inventoryProducts.data?.totalProducts ?? 0,
      icon: <Boxes className="w-6 h-6 text-warning" />,
      bgColor: 'bg-warning/10',
      textColor: 'text-warning',
      subtitle: t('analytics.kpi.low_stock_count', {
        count: inventoryProducts.data?.lowStock ?? 0
      })
    });
  }

  if (visitsEnabled) {
    cards.push({
      title: t('analytics.kpi.total_visits'),
      value: visits.data?.total ?? 0,
      icon: <MapPinned className="w-6 h-6 text-primary" />,
      subtitle: t('analytics.kpi.this_period')
    });
  }

  if (cards.length === 0) {
    return null;
  }

  const isLoading =
    (inventoryProductsEnabled && inventoryProducts.isLoading) ||
    (visitsEnabled && visits.isLoading) ||
    (storesUsersEnabled && storesUsers.isLoading);

  const columns = columnsForCount(cards.length);

  return (
    <StatsCards
      cards={cards}
      loading={isLoading}
      columns={columns}
      loadingRows={columns}
    />
  );
};

export default AnalyticsKpiStrip;
